<template>
    <form id="PersonalInformationForm" class="needs-validation" novalidate>
        <div class="row g-3">
            <!-- Name Field -->
            <div class="col-md-4">
                <label for="applicantName"><strong>{{ $Translate('name') }}: </strong><span
                        class="text-danger">*</span></label>
                <input type="text" id="applicantName" class="form-control" :placeholder="$Translate('namePlaceholder')"
                    :class="{ 'is-valid': personalSubmitted && nameIsValid, 'is-invalid': personalSubmitted && !nameIsValid }"
                    v-model="personalInfo.name" name="applicantName" required />
            </div>

            <!-- Arabic Name Field -->
            <div class="col-md-4">
                <label for="arabicName"><strong>{{ $Translate('arabicName') }}: </strong></label>
                <input type="text" id="arabicName" class="form-control"
                    :placeholder="$Translate('arabicNamePlaceholder')"
                    :class="{ 'is-valid': personalSubmitted && arabicNameIsValid, 'is-invalid': personalSubmitted && !arabicNameIsValid }"
                    v-model="personalInfo.arabicName" name="arabicName" />
            </div>

            <!-- National ID (CPR) Field -->
            <div class="col-md-4">
                <label for="nationalId"><strong>{{ $Translate('nationalId') }}: </strong></label>
                <input type="text" id="nationalId" class="form-control"
                    :placeholder="$Translate('nationalIdPlaceholder')"
                    :class="{ 'is-valid': personalSubmitted && nationalIdIsValid, 'is-invalid': personalSubmitted && !nationalIdIsValid }"
                    v-model="personalInfo.nationalId" name="nationalId" required />
            </div>

            <!-- Country Field -->
            <div class="col-md-4">
                <label for="countryRegionId"><strong>{{ $Translate('country') }}: </strong><span
                        class="text-danger">*</span></label>
                <select class="form-control"
                    :class="{ 'is-valid': personalSubmitted && countryRegionIdIsValid, 'is-invalid': personalSubmitted && !countryRegionIdIsValid }"
                    id="countryRegionId" v-model="personalInfo.countryRegionId" required>
                    <option value="" selected disabled>{{ $Translate('selectCountry') }}</option>
                    <option v-for="country in countryList" :key="country.shortName" :value="country.countryRegionId">
                        {{ country.shortName }}
                    </option>
                </select>
            </div>

            <!-- Gender Field -->
            <div class="col-md-4">
                <label for="gender"><strong>{{ $Translate('gender') }}: </strong><span
                        class="text-danger">*</span></label>
                <select class="form-control"
                    :class="{ 'is-valid': personalSubmitted && genderIsValid, 'is-invalid': personalSubmitted && !genderIsValid }"
                    id="gender" v-model="personalInfo.gender" required>
                    <option value="" selected disabled>{{ $Translate('selectGender') }}</option>
                    <option v-for="gender in genderList.filter(g => g.name !== '')" :key="gender.name"
                        :value="gender.intValNumBN">
                        {{ gender.name }}
                    </option>
                </select>
            </div>

            <!-- Date Of Birth Field -->
            <div class="col-md-4">
                <label for="dateOfBirth"><strong>{{ $Translate('dateOfBirth') }}: </strong><span
                        class="text-danger">*</span></label>
                <input type="date" id="dateOfBirth" class="form-control"
                    :class="{ 'is-valid': personalSubmitted && dateOfBirthIsValid, 'is-invalid': personalSubmitted && !dateOfBirthIsValid }"
                    v-model="personalInfo.dateOfBirth" name="dateOfBirth" required />
            </div>

            <!-- Email Field -->
            <div class="col-md-4">
                <label for="email"><strong>{{ $Translate('email') }}: </strong><span
                        class="text-danger">*</span></label>
                <input type="email" id="email" class="form-control" :placeholder="$Translate('emailPlaceholder')"
                    :class="{ 'is-valid': personalSubmitted && emailIsValid, 'is-invalid': personalSubmitted && !emailIsValid }"
                    v-model="personalInfo.email" name="email" required />
            </div>

            <!-- Confirm Email Field -->
            <div class="col-md-4">
                <label for="confirmEmail"><strong>{{ $Translate('confirmEmail') }}: </strong><span
                        class="text-danger">*</span></label>
                <input type="email" id="confirmEmail" class="form-control"
                    :placeholder="$Translate('confirmEmailPlaceholder')"
                    :class="{ 'is-valid': personalSubmitted && confirmEmailIsValid, 'is-invalid': personalSubmitted && !confirmEmailIsValid }"
                    v-model="personalInfo.confirmEmail" name="confirmEmail" required />
            </div>

            <!-- Mobile Number Field -->
            <div class="col-md-4">
                <label for="phone"><strong>{{ $Translate('phone') }}: </strong><span
                        class="text-danger">*</span></label>
                <input type="text" id="phone" class="form-control" :placeholder="$Translate('phonePlaceholder')"
                    :class="{ 'is-valid': personalSubmitted && phoneIsValid, 'is-invalid': personalSubmitted && !phoneIsValid }"
                    v-model="personalInfo.phone" name="phone" required />
            </div>

            <!-- Password Field -->
            <div class="col-md-4">
                <label for="password"><strong>{{ $Translate('password') }}: </strong><span
                        class="text-danger">*</span></label>
                <input type="password" id="password" class="form-control"
                    :placeholder="$Translate('passwordPlaceholder')"
                    :class="{ 'is-valid': personalSubmitted && passwordIsValid, 'is-invalid': personalSubmitted && !passwordIsValid }"
                    v-model="personalInfo.password" name="password" required />
            </div>

            <!-- Confirm Password Field -->
            <div class="col-md-4">
                <label for="confirmPassword"><strong>{{ $Translate('confirmPassword') }}: </strong><span
                        class="text-danger">*</span></label>
                <input type="password" id="confirmPassword" class="form-control"
                    :placeholder="$Translate('confirmPasswordPlaceholder')"
                    :class="{ 'is-valid': personalSubmitted && confirmPasswordIsValid, 'is-invalid': personalSubmitted && !confirmPasswordIsValid }"
                    v-model="personalInfo.confirmPassword" name="confirmPassword" required />
            </div>

            <!-- LinkedIn Field -->
            <div class="col-md-4">
                <label for="linkedInLink"><strong>{{ $Translate('linkedInLink') }}: </strong></label>
                <input type="text" id="linkedInLink" class="form-control"
                    :placeholder="$Translate('linkedInLinkPlaceholder')"
                    :class="{ 'is-valid': personalSubmitted && linkedInLinkIsValid, 'is-invalid': personalSubmitted && !linkedInLinkIsValid }"
                    v-model="personalInfo.linkedInLink" name="linkedInLink" required />
            </div>

            <!-- Years Of Experience Field -->
            <div class="col-md-4">
                <label for="yearOfExperience"><strong>{{ $Translate('yearsOfExperience') }}: </strong><span
                        class="text-danger">*</span></label>
                <input type="number" id="yearOfExperience" class="form-control" placeholder="0"
                    :class="{ 'is-valid': personalSubmitted && yearOfExperienceIsValid, 'is-invalid': personalSubmitted && !yearOfExperienceIsValid }"
                    v-model="personalInfo.yearOfExperience" name="yearOfExperience" value="0" required />
            </div>
        </div>
    </form>
</template>

<script>
export default {
    name: 'PersonalInfo',
    props: {
        personalInfo: Object,
        personalSubmitted: Boolean,
        countryList: Array,
        genderList: Array,
        nameIsValid: Boolean,
        arabicNameIsValid: Boolean,
        nationalIdIsValid: Boolean,
        countryRegionIdIsValid: Boolean,
        genderIsValid: Boolean,
        dateOfBirthIsValid: Boolean,
        emailIsValid: Boolean,
        confirmEmailIsValid: Boolean,
        phoneIsValid: Boolean,
        passwordIsValid: Boolean,
        confirmPasswordIsValid: Boolean,
        linkedInLinkIsValid: Boolean,
        yearOfExperienceIsValid: Boolean,
    }
}
</script>

<style scoped>
.col-md-4 {
    text-align: start;
}

.col-md-4 label {
    margin-bottom: 10px;
}

.col-md-4 input,
.col-md-4 select,
.col-md-4 textarea {
    margin-bottom: 12px;
}

.col-md-4 select {
    -webkit-appearance: none;
    -moz-appearance: none;
    appearance: none;
    background: url('../../../../dist/assets/select.svg') no-repeat right 0.75rem center;
    background-color: #fff;
    background-size: 16px 16px;
    padding-right: 2rem;
    line-height: 1.5;
    height: calc(1.5em + 0.75rem + 2px);
}
</style>